<template>
  <div>
    <v-row>
      <v-col cols="12" v-if="featureEnabled === false">
        <v-alert type="info" outlined dense>
          {{ $t('agent_tokens.feature_not_included') }}
        </v-alert>
      </v-col>

      <v-col cols="12" v-else>
        <p class="text-body-2 grey--text text--darken-1 mb-1">
          {{ $t('agent_tokens.description') }}
        </p>
        <p class="text-body-2 mb-3">
          <a :href="$t('agent_tokens.help_url')" target="_blank" rel="noopener">
            {{ $t('agent_tokens.help_link') }}
            <v-icon x-small>mdi-open-in-new</v-icon>
          </a>
        </p>

        <v-btn small depressed color="primary" @click="openCreateDialog" :loading="isLoading" class="mb-3">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('agent_tokens.create_button') }}
        </v-btn>

        <v-data-table
          :headers="headers"
          :items="tokens"
          :loading="isLoading"
          :no-data-text="$t('agent_tokens.no_tokens')"
          dense
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:item.token_last4="{ item }">
            <code>rpa_…{{ item.token_last4 }}</code>
          </template>
          <template v-slot:item.user="{ item }">
            {{ item.user && item.user.name ? item.user.name : '—' }}
          </template>
          <template v-slot:item.expires_at="{ item }">
            {{ item.expires_at ? formatDate(item.expires_at) : '—' }}
          </template>
          <template v-slot:item.last_used_at="{ item }">
            {{ item.last_used_at ? formatDate(item.last_used_at) : $t('agent_tokens.never_used') }}
          </template>
          <template v-slot:item.revoked_at="{ item }">
            <v-chip x-small color="error" v-if="item.revoked_at">{{ $t('agent_tokens.revoked') }}</v-chip>
            <v-chip x-small color="warning" v-else-if="isExpired(item)">{{ $t('agent_tokens.expired') }}</v-chip>
            <v-chip x-small color="success" v-else>{{ $t('agent_tokens.active') }}</v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn x-small icon color="error" v-if="!item.revoked_at"
              @click="confirmRevoke(item)" :disabled="isLoading">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <!-- Выпуск ключа -->
        <v-dialog v-model="createDialog" max-width="500" persistent>
          <v-card>
            <v-card-title>{{ $t('agent_tokens.create_title') }}</v-card-title>
            <v-card-text>
              <v-text-field v-model="newTokenName" :label="$t('agent_tokens.name_label')"
                outlined dense :rules="[v => !!v || $t('agent_tokens.name_required')]" autofocus />
              <p class="caption grey--text mb-0">{{ $t('agent_tokens.create_hint') }}</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="createDialog = false" :disabled="isCreating">{{ $t('custom.cancel') }}</v-btn>
              <v-btn color="primary" depressed :loading="isCreating" :disabled="!newTokenName"
                @click="createToken">{{ $t('custom.create') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Ключ показывается ОДИН раз -->
        <v-dialog v-model="plainDialog" max-width="600" persistent>
          <v-card>
            <v-card-title>{{ $t('agent_tokens.plain_title') }}</v-card-title>
            <v-card-text>
              <v-alert type="warning" outlined dense class="mb-3">
                {{ $t('agent_tokens.plain_warning') }}
              </v-alert>
              <v-text-field :value="plainTokenShown" readonly outlined dense
                autocomplete="off" spellcheck="false"
                append-icon="mdi-content-copy" @click:append="copyToken" />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="primary" depressed @click="plainDialog = false">
                {{ $t('agent_tokens.plain_close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Подтверждение отзыва -->
        <v-dialog v-model="revokeDialog" max-width="400">
          <v-card>
            <v-card-title>{{ $t('agent_tokens.revoke_title') }}</v-card-title>
            <v-card-text>
              {{ $t('agent_tokens.revoke_confirm', { name: tokenToRevoke && tokenToRevoke.name }) }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="revokeDialog = false">{{ $t('custom.cancel') }}</v-btn>
              <v-btn color="error" depressed :loading="isLoading" @click="revokeToken">
                {{ $t('agent_tokens.revoke_button') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </div>
</template>

<script>
// FT-155 REQ-10 — персональные ключи агента (MCP) в профиле сотрудника. Образец —
// SitesBuilderTokens.vue, но гейт другой: доступность услуги отдаёт сам API
// (GET /api/v1/agent_access_tokens → feature_enabled), а не флаг плана из store —
// у услуги нет триала, и сервер уже знает ответ.
// Plain-ключ приходит один раз в ответе create и после закрытия окна не хранится.
import axios from 'axios';
import moment from 'moment';

export const API_PATH = '/api/v1/agent_access_tokens';

// Код ошибки API → ключ локали. Неизвестный код → общее сообщение.
export const ERROR_KEYS = {
  feature_not_included: 'agent_tokens.feature_not_included',
  role_not_allowed: 'agent_tokens.role_not_allowed',
  too_many_tokens: 'agent_tokens.too_many_tokens',
  no_branch: 'agent_tokens.no_branch',
};

export default {
  name: 'AgentAccessTokens',
  data() {
    return {
      featureEnabled: null,
      tokens: [],
      isLoading: false,
      isCreating: false,
      createDialog: false,
      plainDialog: false,
      revokeDialog: false,
      newTokenName: '',
      plainTokenShown: '',
      tokenToRevoke: null,
    };
  },
  watch: {
    plainDialog(visible) {
      if (!visible) {
        this.plainTokenShown = '';
      }
    },
  },
  computed: {
    headers() {
      return [
        { text: this.$t('agent_tokens.name'), value: 'name' },
        { text: this.$t('agent_tokens.last4'), value: 'token_last4', sortable: false },
        { text: this.$t('agent_tokens.owner'), value: 'user', sortable: false },
        { text: this.$t('agent_tokens.status'), value: 'revoked_at', sortable: false },
        { text: this.$t('agent_tokens.expires_at'), value: 'expires_at' },
        { text: this.$t('agent_tokens.last_used_at'), value: 'last_used_at' },
        { text: '', value: 'actions', sortable: false },
      ];
    },
  },
  mounted() {
    this.loadTokens();
  },
  methods: {
    formatDate(iso) {
      return moment(iso).format('YYYY-MM-DD HH:mm');
    },
    isExpired(token) {
      return !!token.expires_at && moment(token.expires_at).isBefore(moment());
    },
    errorMessageFor(err, fallbackKey) {
      const code = err && err.response && err.response.data && err.response.data.errors
        && err.response.data.errors[0] && err.response.data.errors[0].code;
      return this.$t(ERROR_KEYS[code] || fallbackKey);
    },
    async loadTokens() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(API_PATH);
        this.featureEnabled = data.feature_enabled !== false;
        this.tokens = data.tokens || [];
      } catch (err) {
        this.$notify(this.$t('agent_tokens.load_error'), '', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    openCreateDialog() {
      this.newTokenName = '';
      this.createDialog = true;
    },
    async createToken() {
      if (!this.newTokenName) return;
      this.isCreating = true;
      try {
        const { data } = await axios.post(API_PATH, { name: this.newTokenName });
        this.tokens.unshift(data.token);
        this.plainTokenShown = data.plain_token;
        this.createDialog = false;
        this.plainDialog = true;
      } catch (err) {
        this.$notify(this.errorMessageFor(err, 'agent_tokens.create_error'), '', 'error');
      } finally {
        this.isCreating = false;
      }
    },
    confirmRevoke(token) {
      this.tokenToRevoke = token;
      this.revokeDialog = true;
    },
    async revokeToken() {
      if (!this.tokenToRevoke) return;
      this.isLoading = true;
      try {
        await axios.delete(`${API_PATH}/${this.tokenToRevoke.id}`);
        await this.loadTokens();
        this.revokeDialog = false;
        this.$notify(this.$t('agent_tokens.revoked_success'), '', 'success');
      } catch (err) {
        this.$notify(this.errorMessageFor(err, 'agent_tokens.revoke_error'), '', 'error');
      } finally {
        this.isLoading = false;
        this.tokenToRevoke = null;
      }
    },
    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.plainTokenShown);
        this.$notify(this.$t('agent_tokens.copied'), '', 'success');
      } catch (err) {
        this.$notify(this.$t('agent_tokens.copy_failed'), '', 'warning');
      }
    },
  },
};
</script>
